// inspired by https://d3-graph-gallery.com/graph/heatmap_basic.html
import {
    select as D3select,
    scaleBand as D3scaleBand,
    scaleLinear as D3scaleLinear,
    dispatch as D3dispatch,
    scaleTime as D3scaleTime,
    axisBottom as D3axisBottom,
    axisLeft as D3axisLeft,
    interpolateRgb as D3interpolateRgb,
    utcMinute as D3minute,
} from 'd3';
import D3Tooltips from './D3Tooltips.js';
import Moment from 'moment/moment';
import { CRITICAL, HIGH, MEDIUM, LOW } from '../components/charts/heatmap';

// a d3 timeline-heatmap component
// expects data:
// [{
//     timestamp: new Date(),
//     series: 'v1',
//     value: 10,
// }]

// [
//     {
//         timestamp: new Date(),
//         series: 'critical',
//         value: 10,
//     },
//     {
//         timestamp: new Date(),
//         series: 'high',
//         value: 5,
//     }
// ]

// series:
// [{ value: 1, label: 'Critical' }]
const D3Heatmap = function () {
    const dispatch = D3dispatch('click', 'mouseover');
    // current time in milliseconds rounded to the nearest 10 minutes
    const now = Math.floor(Date.now() / (10 * 60 * 1000)) * (10 * 60 * 1000);

    // default settings can be overridden
    let theme;
    let height = 110;
    let width = 1000;
    let key = 'heatmap';
    let tip = D3Tooltips();
    let start = now - 7 * 24 * 60 * 60 * 1000; // default to 7 days ago
    let end = now; // default to now
    let interval = 30; // default to 30 minutes
    let series = [CRITICAL, HIGH, MEDIUM, LOW]; // default series

    function my (selection) {
        if (!selection) return;
        // set the dimensions and margins of the graph
        const margin = { top: 20, right: 0, bottom: 20, left: 50 };

        const roundDate = (d, up, dfault) => {
            if (!d || !interval) return dfault;
            const ms = 1000 * 60 * interval; // convert minutes to ms
            return up ? new Date(Math.ceil(d / ms) * ms)
                : new Date(Math.floor(d.getTime() / ms) * ms);
        };

        selection.each(function (data, i) {
            // truncate start to nearest interval
            start = roundDate(start, false, new Date(now - 14 * 24 * 60 * 60 * 1000));
            // round up end to nearest interval
            end = roundDate(end, true, new Date(now));

            // fill in blanks where there is no data
            // mapify the data
            const have = (data || []).reduce((a, c) => ({ ...a, [`${c.timestamp.getTime()}::${c.series}`]: c }), {});
            // then create a new array with all timestamps in the range
            let continueous = [];
            for (let d = new Date(start); d <= end; d = new Date(d.getTime() + interval * 60000)) {
                const timestamp = d.getTime();
                series.forEach(s => {
                    continueous.push(have[`${timestamp}::${s.value}`] || {
                        timestamp,
                        value: 0, // empty value
                        series: s.value,
                    });
                });
            }

            // filter data to only include timestamps within the range
            // the React component might update the range before the data is updated
            continueous = continueous.filter(d => {
                const timestamp = new Date(d.timestamp);
                return timestamp >= start && timestamp <= end;
            });

            // build the scales
            const x = D3scaleTime()
                .range([margin.left, width - margin.right])
                .domain([new Date(start), new Date(end)]);
            x.ticks(D3minute.every(interval));

            // use a pre-determined series because we want to show all series even if they have no data
            // you can also derive from data data.map(d => d.series) if you don't need placeholders
            const y = D3scaleBand()
                .range([margin.top, height - margin.bottom])
                .domain(series
                    .sort((a, b) => a.value - b.value) // sort by order if available
                    .map(d => d.value))
                .padding(0.01);

            // y-axis is mapped to colors
            // I think it would be better to use a color scale for the values
            const colorScale = D3scaleLinear()
                .range(theme.HeatmapColors)
                .domain(y.domain())
                .interpolate(D3interpolateRgb);

            // get width for x minute intervals between start and end over width
            const mwidth = (width - margin.left - margin.right) / ((end - start) / 60000);
            const xsquare = interval * mwidth - 3; // -padding
            const ysquare = y.bandwidth() - 3; // Math.max(y.bandwidth(), 10) - 1; // -padding
            // const square = Math.min(xsquare, ysquare); // use the smaller of the two

            // add the main svg one time
            const svgj = D3select('.heatmap').selectAll('svg.container')
                .data([continueous], function (d) { return 'once' });
            const svg = svgj.enter()
                .append('svg')
                .attr('class', 'container')
                .merge(svgj)
                .attr('width', width)
                .attr('height', height)
                .style('overflow', 'visible'); // allow overflow for tooltips;

            // add the g for the data one time
            const once = svg.selectAll('g').data(['once']);
            const gdata = once.enter().append('g').merge(once);
            gdata.selectAll('rect.data')
                .data(continueous, d => d.timestamp + ':' + d.series) // unique key for each rect
                .join('rect')
                .attr('class', 'data')
                .attr('x', d => x(d.timestamp))
                .attr('y', d => y(d.series))
                .attr('opacity', d => d.value > 0 ? 1 : 0.1)
                .style('transform', '0.3s ease-in-out')
                .on('mousemove', function (event, d) {
                    if (!d.value) return;
                    // "this" is only available when using the function syntax
                    // D3select(this).style('opacity', 0.5);
                    D3select(this).attr('width', xsquare + 2);
                    D3select(this).attr('height', ysquare + 2);
                    D3select(this).attr('x', x(d.timestamp) - 1);
                    D3select(this).attr('y', y(d.series) - 1);

                    D3select(this).classed('hover', true);
                    // move the tooltip, will display if hidden
                    const label = series
                        .find(s => s.value === d.series) || d;
                    tip.move(x(d.timestamp), y(d.series), [[
                        `${label.label}`,
                        `${d.value}`,
                        `${Moment(d.timestamp).format(theme.date.short)}`,
                    ]]);
                })
                .on('mouseout', function (event, d) {
                    // D3select(this).style('opacity', 1);
                    D3select(this).attr('width', xsquare);
                    D3select(this).attr('height', ysquare);
                    D3select(this).attr('x', x(d.timestamp));
                    D3select(this).attr('y', y(d.series));
                    D3select(this).classed('hover', false);
                    tip.hide();
                })
                .transition()
                .duration(theme.transitions.duration.complex)
                .attr('width', xsquare)
                .attr('height', ysquare)
                .style('fill', d => colorScale(d.series));

            // x-axis
            svg.selectAll('g.x-axis').data(['x-axis']).join('g')
                .attr('class', 'x-axis')
            // add a little space above the x-axis and line up with the middle of the square
                .attr('transform', `translate(${xsquare / 2},${height - margin.top + 5})`)
                .call(D3axisBottom(x)
                    // add a vertical grid for example
                    // .tickSizeInner(-height + margin.top)
                )
            // remove the x-axis line
                .call(g => g.select('.domain').remove());

            // y-axis
            svg.selectAll('g.y-axis').data(['y-axis']).join('g')
                .attr('class', 'y-axis')
                .attr('transform', `translate(${margin.left},${-5 + ysquare / 4})`)
                .call(D3axisLeft(y)
                    .tickSize(0) // no ticks
                    .tickPadding(10)
                    .tickFormat(d => {
                        // use the label from the series or the value
                        const label = series.find(s => s.value === d);
                        return label ? label.label : d;
                    })
                )
                .call(g => g.select('.domain').remove())
                .call(g => g.selectAll('.tick > text')
                    .style('fill', d => colorScale(d)));

            // right side y-axis
            svg.selectAll('g.y-axis-right').data(['y-axis-right']).join('g')
                .attr('class', 'y-axis-right')
                .attr('transform', `translate(${width},${-5 + ysquare / 4})`)
                .call(D3axisLeft(y)
                    .tickSize(0) // no ticks
                    .tickPadding(-35)
                    .tickFormat(d => {
                        // total value for the series
                        return continueous
                            .filter(c => c.series === d)
                            .reduce((a, c) => a + c.value, 0);
                    })
                )
                .call(g => g.select('.domain').remove())
                .call(g => g.selectAll('.tick > text')
                    .style('fill', d => colorScale(d)));

            // tooltips
            tip = tip.theme(theme)
                .bounds({ width, height });

            // scoot tooltip above and middle of the square
            svg.selectAll('g.heatmap-tip').data(['heatmap-tip']).join('g')
                .attr('class', 'heatmap-tip')
                .attr('transform', `translate(${xsquare / 2},${-margin.top - 5})`)
                .call(tip);
        });
    }

    my.theme = function (v) {
        if (!arguments.length) return theme;
        theme = v || theme;
        return my;
    };

    my.on = function () {
        const value = dispatch.on.apply(dispatch, arguments);
        return value === dispatch ? my : value;
    };

    my.height = function (v) {
        if (!arguments.length) return height;
        height = v;
        return my;
    };

    my.width = function (v) {
        if (!arguments.length) return width;
        width = v;
        return my;
    };

    my.key = function (v) {
        if (!arguments.length) return key;
        key = v;
        return my;
    };

    my.start = function (v) {
        if (!arguments.length) return start;
        start = v;
        return my;
    };

    my.end = function (v) {
        if (!arguments.length) return end;
        end = v;
        return my;
    };

    my.interval = function (v) {
        if (!arguments.length) return interval;
        interval = v;
        return my;
    };

    my.series = function (v) {
        if (!arguments.length) return series;
        series = v;
        return my;
    };

    return my;
};

export default D3Heatmap;
